// src/services/pdf/sections/ClinicalSummarySection.ts
// Renders Section 2: Six-week clinical summary across 5 NICE NG194 domains.

import { PDFDocument } from "../core/PDFDocument";
import { PDF_COLORS } from "../core/colors";
import { PAGE_LAYOUT } from "../core/layout";
import type { PDFRenderData } from "../types";
import type { SectionRenderer } from "./SectionRenderer";
import type { MoodWeek, SymptomSummary, BleedingDay } from "../../../types/gpSummary";

export class ClinicalSummarySection implements SectionRenderer {
  readonly id = "clinicalSummary";

  render(doc: PDFDocument, data: PDFRenderData): void {
    const { summaryData, toggles } = data;

    doc.ensureSpace(10);
    doc.setFont("bold", PAGE_LAYOUT.fontSize.sectionTitle);
    doc.setColor(PDF_COLORS.brandPrimary);
    doc.text("SIX-WEEK CLINICAL SUMMARY", doc.contentLeft);
    doc.advanceY(6);

    // Domain 1: Mental Health (Mood)
    if (toggles.mood) {
      this.renderMoodDomain(doc, summaryData.mood, summaryData.moodAverage, summaryData.moodTrendText);
    }

    // Domain 2 & 3: Symptoms (pelvic + physical combined)
    if (toggles.symptoms) {
      this.renderSymptomsDomain(doc, summaryData.symptoms);
    }

    // Domain 4: Bleeding
    if (toggles.bleeding) {
      this.renderBleedingDomain(doc, summaryData.bleedingLog);
    }

    // Domain 5: Activities
    if (toggles.activities) {
      this.renderActivitiesDomain(doc, data);
    }
  }

  // ─── Domain 1: Mental Health ──────────────────────────────────────────────

  private renderMoodDomain(
    doc: PDFDocument,
    mood: MoodWeek[],
    avg: string,
    trendText: string
  ): void {
    doc.ensureSpace(20);
    this.renderDomainTitle(doc, "Domain 1 - Mental Health");

    if (mood.length === 0) {
      doc.writeLine("No mood data recorded.", {
        size: PAGE_LAYOUT.fontSize.body,
        style: "italic",
        color: PDF_COLORS.textMuted,
      });
      doc.advanceY(PAGE_LAYOUT.subsectionGap);
      return;
    }

    // Mood scores table
    const colWidth = doc.contentWidth / Math.max(mood.length, 1);
    const startX = doc.contentLeft;

    // Week headers
    doc.setFont("bold", PAGE_LAYOUT.fontSize.tiny);
    doc.setColor(PDF_COLORS.textMuted);
    for (let i = 0; i < mood.length; i++) {
      doc.text(mood[i].week, startX + i * colWidth + colWidth / 2, { align: "center" });
    }
    doc.advanceY(4);

    // Scores
    doc.setFont("bold", PAGE_LAYOUT.fontSize.body);
    for (let i = 0; i < mood.length; i++) {
      const m = mood[i];
      const color = m.score >= 3.5 ? PDF_COLORS.green : m.score >= 2.5 ? PDF_COLORS.amber : PDF_COLORS.red;
      doc.setColor(color);
      doc.text(`${m.score}`, startX + i * colWidth + colWidth / 2, { align: "center" });
    }
    doc.advanceY(4);

    // Labels
    doc.setFont("normal", PAGE_LAYOUT.fontSize.tiny);
    doc.setColor(PDF_COLORS.textMuted);
    for (let i = 0; i < mood.length; i++) {
      doc.text(mood[i].label, startX + i * colWidth + colWidth / 2, { align: "center" });
    }
    doc.advanceY(5);

    // Average and trend
    doc.setFont("bold", PAGE_LAYOUT.fontSize.body);
    doc.setColor(PDF_COLORS.textPrimary);
    doc.text(`6-week average: ${avg}/5`, startX);
    doc.advanceY(4);

    doc.setFont("italic", PAGE_LAYOUT.fontSize.small);
    doc.setColor(PDF_COLORS.textSecondary);
    doc.writeWrapped(trendText, { size: PAGE_LAYOUT.fontSize.small, style: "italic" });
    doc.advanceY(PAGE_LAYOUT.subsectionGap);
  }

  // ─── Domain 2 & 3: Symptoms ───────────────────────────────────────────────

  private renderSymptomsDomain(doc: PDFDocument, symptoms: SymptomSummary[]): void {
    doc.ensureSpace(15);
    this.renderDomainTitle(doc, "Domain 2/3 - Pelvic Health & Physical Recovery");

    if (symptoms.length === 0) {
      doc.writeLine("No symptom progression data available.", {
        size: PAGE_LAYOUT.fontSize.body,
        style: "italic",
        color: PDF_COLORS.textMuted,
      });
      doc.advanceY(PAGE_LAYOUT.subsectionGap);
      return;
    }

    // Table header
    const nameCol = doc.contentLeft;
    const statusCol = doc.contentLeft + 55;
    const fromCol = doc.contentLeft + 95;
    const toCol = doc.contentLeft + 135;

    doc.setFont("bold", PAGE_LAYOUT.fontSize.tiny);
    doc.setColor(PDF_COLORS.textMuted);
    doc.text("SYMPTOM", nameCol);
    doc.text("STATUS", statusCol);
    doc.text("FROM", fromCol);
    doc.text("TO", toCol);
    doc.advanceY(4);

    doc.drawLine(nameCol, doc.y, doc.contentRight);
    doc.advanceY(2);

    // Symptom rows
    for (const symptom of symptoms) {
      doc.ensureSpace(6);
      this.renderSymptomRow(doc, symptom, nameCol, statusCol, fromCol, toCol);
    }

    doc.advanceY(PAGE_LAYOUT.subsectionGap);
  }

  private renderSymptomRow(
    doc: PDFDocument,
    symptom: SymptomSummary,
    nameCol: number,
    statusCol: number,
    fromCol: number,
    toCol: number
  ): void {
    // Severity dot
    const dotColor = symptom.status === "worsening" ? PDF_COLORS.red
      : symptom.status === "improving" ? PDF_COLORS.green
      : symptom.status === "resolved" ? PDF_COLORS.green
      : PDF_COLORS.textMuted;
    doc.drawCircle(nameCol + 1.5, doc.y - 0.5, 1, dotColor);

    // Name
    doc.setFont("normal", PAGE_LAYOUT.fontSize.small);
    doc.setColor(PDF_COLORS.textPrimary);
    doc.text(symptom.name, nameCol + 5);

    // Status badge text
    const statusLabel = symptom.status === "resolved" ? "Resolved"
      : symptom.status === "improving" ? `+${symptom.percent}%`
      : symptom.status === "worsening" ? "Worsening"
      : "Stable";
    const statusColor = symptom.status === "worsening" ? PDF_COLORS.red
      : symptom.status === "resolved" || symptom.status === "improving" ? PDF_COLORS.green
      : PDF_COLORS.textSecondary;
    doc.setFont("bold", PAGE_LAYOUT.fontSize.small);
    doc.setColor(statusColor);
    doc.text(statusLabel, statusCol);

    // From/To
    doc.setFont("normal", PAGE_LAYOUT.fontSize.tiny);
    doc.setColor(PDF_COLORS.textSecondary);
    doc.text(symptom.from, fromCol, { maxWidth: 35 });
    doc.text(symptom.to, toCol, { maxWidth: 35 });

    doc.advanceY(5);
  }

  // ─── Domain 4: Bleeding ───────────────────────────────────────────────────

  private renderBleedingDomain(doc: PDFDocument, bleedingLog: BleedingDay[]): void {
    doc.ensureSpace(18);
    this.renderDomainTitle(doc, "Domain 4 - Vaginal Bleeding (Lochia)");

    const bleedingDays = bleedingLog.filter((d) => d.flow !== "none");
    const heavyDays = bleedingLog.filter((d) => d.flow === "heavy");
    const lastBleeding = [...bleedingLog].reverse().find((d) => d.flow !== "none");
    const clotDays = bleedingLog.filter(
      (d) => d.clots === "medium" || d.clots === "large" || d.clots === "frequent"
    );

    // Summary stats
    doc.setFont("normal", PAGE_LAYOUT.fontSize.body);
    doc.setColor(PDF_COLORS.textPrimary);
    doc.writeLine(`Days with bleeding: ${bleedingDays.length}/42`);
    doc.writeLine(`Heavy flow days: ${heavyDays.length}`);
    doc.writeLine(
      `Last bleeding: ${lastBleeding ? `Day ${lastBleeding.day} (Week ${lastBleeding.week})` : "None recorded"}`
    );
    doc.writeLine(`Clotting events (medium+): ${clotDays.length}`);

    // Lochia resolution check
    const resolvedDays = this.getConsecutiveNonBleedingDays(bleedingLog);
    if (resolvedDays >= 7) {
      doc.setFont("bold", PAGE_LAYOUT.fontSize.small);
      doc.setColor(PDF_COLORS.green);
      doc.writeLine("Lochia appears resolved (>=7 consecutive days without bleeding)");
    } else if (bleedingDays.length > 0) {
      doc.setFont("bold", PAGE_LAYOUT.fontSize.small);
      doc.setColor(PDF_COLORS.amber);
      doc.writeLine("! Bleeding still present - discuss with GP");
    }

    // Heavy flow detail
    if (heavyDays.length > 0) {
      doc.setFont("normal", PAGE_LAYOUT.fontSize.small);
      doc.setColor(PDF_COLORS.textSecondary);
      const heavyDetail = heavyDays.map((d) => `Day ${d.day}`).join(", ");
      doc.writeLine(`Heavy flow on: ${heavyDetail}`);
    }

    doc.advanceY(PAGE_LAYOUT.subsectionGap);
  }

  private getConsecutiveNonBleedingDays(log: BleedingDay[]): number {
    let maxStreak = 0;
    let currentStreak = 0;
    for (const day of log) {
      if (day.flow === "none") {
        currentStreak++;
        maxStreak = Math.max(maxStreak, currentStreak);
      } else {
        currentStreak = 0;
      }
    }
    return maxStreak;
  }

  // ─── Domain 5: Activities ─────────────────────────────────────────────────

  private renderActivitiesDomain(doc: PDFDocument, data: PDFRenderData): void {
    const { summaryData } = data;
    const { weeklyActivities, snapshot } = summaryData;

    doc.ensureSpace(12);
    this.renderDomainTitle(doc, "Domain 5 - Programme Engagement");

    const totalDone = weeklyActivities.reduce((s, w) => s + w.completedCount, 0);
    const totalAll = weeklyActivities.reduce((s, w) => s + w.totalCount, 0);
    const pct = totalAll > 0 ? Math.round((totalDone / totalAll) * 100) : 0;

    doc.setFont("normal", PAGE_LAYOUT.fontSize.body);
    doc.setColor(PDF_COLORS.textPrimary);
    doc.writeLine(`Programme completion: ${totalDone}/${totalAll} activities (${pct}%)`);
    doc.writeLine(`Completed weeks: ${snapshot.completedWeek}/6`);
    doc.writeLine(`Journal entries logged: ${snapshot.journalEntryCount}`);

    doc.advanceY(PAGE_LAYOUT.subsectionGap);
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  private renderDomainTitle(doc: PDFDocument, title: string): void {
    doc.setFont("bold", PAGE_LAYOUT.fontSize.subsectionTitle);
    doc.setColor(PDF_COLORS.textPrimary);
    doc.text(title, doc.contentLeft);
    doc.advanceY(5);
  }
}
